import * as THREE from 'three';
import type { CombatVisualEvent } from '../../../shared/game/combat-types.js';
import { effectMaterial, effectParticles, pathRibbon, shadeEffect, spiralRibbon } from './effect-surfaces.js';
import { lightningDischarge, lightningImpact } from './lightning-discharge.js';
import { howlWave } from './howl-wave.js';
import { sparkStep } from './spark-step.js';
import { sneakReveal } from './sneak-reveal.js';
import { sneakShimmer } from './sneak-shimmer.js';
import { retaliationDistortion } from './retaliation-distortion.js';
import { smokePuff } from './smoke-puff.js';

export interface SkillEffect {
  group: THREE.Group;
  /** Milliseconds, retained for effect callers outside the scene. */
  duration: number;
  /** Shares the channel's fixed light pool; effects never add lights to the scene. */
  lights?: { position: THREE.Vector3; color: THREE.Color; output: number }[];
  update(progress: number, from: THREE.Vector3, to: THREE.Vector3, time?: number, ppm?: number): void;
  /** A joint of the target's figure the effect emanates from (a wolf's jaw), followed as the figure moves. */
  anchor?: string;
}
export interface EffectDimensions { actorHeight: number; targetHeight: number; targetRadius: number; launchAtOrb?: boolean }
const AMBER = 0xdba55d, BLUE = 0x9bc9e0, RUST = 0xc4683f, GREEN = 0x91cfa0, VIOLET = 0xc5a4ff, VEIL = 0x2e3236, TAU = Math.PI * 2;
const envelope = (p: number) => Math.sin(Math.PI * THREE.MathUtils.clamp(p, 0, 1)) ** .65;

function ribbon(parent: THREE.Group, radius: number, height: number, color: number, phase = 0, turns = 1, width = .12) {
  const mesh = new THREE.Mesh(spiralRibbon(radius, height, turns, width, phase), effectMaterial(color, 0xfff3ce)); parent.add(mesh); return mesh;
}
function shell(parent: THREE.Group, radius: number, height: number, color: number) {
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(1, 32, 20), effectMaterial(color, 0xf4ffdd, 1, .015));
  mesh.scale.set(radius, height / 2, radius); mesh.position.y = height / 2; parent.add(mesh); return mesh;
}

/** Restrained, dimensional Quarry emblem and moving amber ground trace. */
export function createQuarryVisual(radius: number, height = 2) {
  const group = new THREE.Group(); group.name = 'hunters-mark-quarry';
  const trace = new THREE.Group(); trace.name = 'quarry-ground-trace'; trace.position.y = .08; group.add(trace);
  for (let i = 0; i < 3; i++) {
    const mesh = ribbon(trace, radius + .16, .018, AMBER, i * TAU / 3, .23, .045); mesh.userData.baseOpacity = .4;
  }
  const emblem = new THREE.Group(); emblem.name = 'quarry-emblem'; emblem.position.y = height + .26; group.add(emblem);
  const diamond = new THREE.Mesh(new THREE.OctahedronGeometry(.2, 0), effectMaterial(AMBER, 0xffe6b0, 3));
  diamond.scale.set(.75, 1.35, .75); diamond.userData.baseOpacity = .5; emblem.add(diamond);
  for (const sign of [-1, 1]) {
    const talon = ribbon(emblem, .24, .15, AMBER, sign < 0 ? Math.PI : 0, .25, .055); talon.rotation.z = sign * .3;
    talon.position.y = -.06; talon.userData.baseOpacity = .45;
  }
  return group;
}
export function updateQuarryVisual(group: THREE.Group, time: number, ppm = 40) {
  const emblem = group.getObjectByName('quarry-emblem'); if (emblem) emblem.rotation.y = time * .35;
  const trace = group.getObjectByName('quarry-ground-trace'); if (trace) trace.rotation.y = time * -.25;
  shadeEffect(group, time, .65 + Math.sin(time * 2.1) * .08, 0, ppm);
}

/** Power graphics use mesh surfaces and sparse GPU particles; defensive/tactical actions use their character animation. */
export function createSkillEffect(event: CombatVisualEvent, dimensions: EffectDimensions = { actorHeight: 2, targetHeight: 2, targetRadius: .5 }): SkillEffect | null {
  if (event.kind === 'block' || event.kind === 'interpose' || event.kind === 'assess' || event.kind === 'anticipate') return null;
  const group = new THREE.Group(); group.name = `skill-${event.kind}`;
  const h = dimensions.targetHeight, radius = dimensions.targetRadius;
  const fromAt = new THREE.Vector3(), toAt = new THREE.Vector3();
  const finish = (p: number, time: number, ppm: number) => shadeEffect(group, time, envelope(p), p, ppm);
  if (event.kind === 'impact' && event.damageType === 'lightning') {
    return lightningImpact(h, radius);
  }
  if (event.kind === 'heal') {
    const body = new THREE.Group(); group.add(body); shell(body, radius * .82, h * .92, GREEN);
    for (let i = 0; i < 3; i++) ribbon(body, radius * (.78 + i * .09), h * .8, i === 1 ? 0xe4cb7b : GREEN, i * TAU / 3, 1.1, .1);
    body.add(effectParticles(0xd8eeb0, 9, radius, h * .95));
    return { group, duration: 1600, update(p, _from, to, time = p * 1.6, ppm = 40) {
      group.position.copy(to); body.rotation.y = time * .65; body.scale.setScalar(.85 + Math.sin(p * Math.PI) * .15); finish(p, time, ppm);
    } };
  }
  if (event.kind === 'mark') {
    const links = Array.from({ length: 3 }, () => { const path = pathRibbon(AMBER, .035, 2, 0xffe5b0); group.add(path.mesh); return path; });
    const convergence = new THREE.Group(); group.add(convergence);
    for (let i = 0; i < 3; i++) ribbon(convergence, radius + .18, h * .72, AMBER, i * TAU / 3, .6, .065);
    convergence.add(effectParticles(AMBER, 6, radius + .15, h * .6));
    return { group, duration: 1200, update(p, from, to, time = p * 1.2, ppm = 40) {
      fromAt.copy(from).y += dimensions.actorHeight * .65; toAt.copy(to).y += h * .58;
      links.forEach((link, i) => { link.update(fromAt, toAt, time, false, i * TAU / 3); link.mesh.visible = p < .82; });
      convergence.position.copy(to); convergence.rotation.y = -time * 1.2;
      convergence.scale.setScalar(1.55 - Math.min(1, p * 2) * .55); finish(p, time, ppm);
      links.forEach(link => shadeEffect(link.mesh, time, envelope(p), Math.min(1, p / .68), ppm));
    } };
  }
  // A Captain's Strict Training reuses Coordinated Strike's link, in bandit rust and iron.
  if (event.kind === 'command' || event.kind === 'ability' && event.abilityId === 'strict-training') {
    const bandit = event.kind === 'ability', color = bandit ? RUST : BLUE, sparks = bandit ? 0xf0c09a : 0xd1edff;
    if (bandit) group.name = 'skill-strict-training';
    const link = pathRibbon(color, .075, 2, bandit ? 0xffe2c4 : 0xffffff); link.mesh.name = 'ally-link'; group.add(link.mesh);
    const accents = [new THREE.Group(), new THREE.Group()]; group.add(...accents);
    accents.forEach((accent, i) => {
      const height = i ? h : dimensions.actorHeight;
      for (let j = 0; j < 2; j++) ribbon(accent, .34, height * .5, color, j * Math.PI, .6, .07);
      accent.add(effectParticles(sparks, 5, .4, height * .7));
    });
    return { group, duration: 1250, update(p, from, to, time = p * 1.25, ppm = 40) {
      fromAt.copy(from).y += dimensions.actorHeight * .5; toAt.copy(to).y += h * .5;
      link.update(fromAt, toAt, time); link.mesh.visible = p < .75;
      accents[0].position.copy(from); accents[1].position.copy(to);
      accents.forEach((accent, i) => { accent.rotation.y = time * (i ? -1 : 1); accent.scale.setScalar(.85 + Math.sin(p * Math.PI) * .2); });
      finish(p, time, ppm); shadeEffect(link.mesh, time, envelope(p), Math.min(1, p / .65), ppm);
      shadeEffect(accents[1], time, envelope(p) * THREE.MathUtils.smoothstep(p, .25, .6), p, ppm);
    } };
  }
  if (event.kind === 'teleport') return sparkStep(dimensions);
  if (event.kind === 'arc') {
    return lightningDischarge(dimensions);
  }
  if (event.kind === 'ability') {
    if (event.abilityId === 'summon-after-attack') return smokePuff(h, radius);
    if (event.abilityId === 'survival-retaliation') return retaliationDistortion(h, radius);
    if (event.abilityId === 'sneak-attack') return sneakShimmer(dimensions.actorHeight, .5);
    return null;
  }
  // Dazed, Prone and Frightened are lasting status cues (`status-effects.ts`), not one-shot effects.
  if (event.kind === 'condition') {
    const label = event.label ?? '', dust = label === 'SKEDADDLE';
    const color = dust ? 0xbaa484 : event.abilityId === 'sneak' ? 0xe5e1ca : VIOLET;
    // A hero hiding draws a breath of dark smoke about itself; spotted, the veil tears away in a quicker puff.
    if (event.abilityId === 'hide' || event.abilityId === 'spotted') return smokePuff(h * .75, radius, { color: VEIL, seconds: event.abilityId === 'hide' ? 1.3 : .8, size: .6, name: `${event.abilityId}-veil` });
    // Setting off at a run kicks up a low scuff of dust, where Hide draws its veil about the whole figure.
    if (event.abilityId === 'run') return smokePuff(h * .22, radius, { color: 0xbaa484, seconds: .8, size: .5, name: 'run-dust' });
    if (label === 'FEARSOME HOWL') return howlWave();
    if (event.abilityId === 'sneak') return sneakReveal(h, radius);
    if (dust) {
      ribbon(group, radius * 1.2, .12, color, 0, .95, .14); group.add(effectParticles(color, 8, radius * 1.4, .35));
    } else {
      for (let i = 0; i < 2; i++) ribbon(group, radius + .1, h * .8, color, i * Math.PI, .6, .085);
      shell(group, radius, h * .85, color); group.add(effectParticles(color, 5, radius, h * .7));
    }
    return { group, duration: 1100, update(p, _from, to, time = p * 1.1, ppm = 40) {
      group.position.copy(to); group.rotation.y = time * -.7;
      group.scale.setScalar(.8 + Math.sin(p * Math.PI) * .25); finish(p, time, ppm);
    } };
  }
  return null;
}

export function fadeEffect(group: THREE.Object3D, opacity: number) {
  group.traverse(child => {
    if (!(child instanceof THREE.Mesh || child instanceof THREE.Points || child instanceof THREE.Line)) return;
    for (const material of [child.material].flat()) {
      if (material instanceof THREE.ShaderMaterial && material.uniforms.uOpacity) material.uniforms.uOpacity.value *= opacity;
      else material.opacity = opacity;
    }
  });
}
